import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { CustomerDTO, CustomerPayload } from "../types/dtos";

export const customersApi = createApi({
  reducerPath: "customersApi",
  baseQuery: fetchBaseQuery({ baseUrl: "/api" }),
  tagTypes: ["Customers"],
  endpoints: (b) => ({
    // Options for selectors (id/label/sub)
    getAllCustomers: b.query<
      { id: number; label: string; sub: string | null; archived?: boolean; disabled?: boolean }[],
      { includeEx?: boolean; siteId?: number } | void
    >({
      query: (arg) => {
        const includeEx =
          typeof arg === "object" && arg !== null
            ? Boolean((arg as { includeEx?: boolean }).includeEx)
            : false;
        const suffix = includeEx ? "?includeEx=1" : "";
        return { url: `customers${suffix}` };
      },
      transformResponse: (response: CustomerDTO[]) =>
        response.map((customer) => {
          const archived = Boolean(customer.ex);
          return {
            id: customer.AutoID,
            label: customer.NameInternal ?? "",
            sub: customer.Email ?? null,
            address: [
              customer.AdressAdd,
              customer.Street,
              customer.HouseNumber,
              `${customer.ZipCode ?? ""} ${customer.City ?? ""}`.trim(),
            ]
              .filter(Boolean)
              .join(", "),
            archived,
            disabled: archived,
          };
        }),
      providesTags: [{ type: "Customers", id: "LIST" }],
    }),
    // Liste (hast du bereits)
    getCustomersRaw: b.query<CustomerDTO[], void>({
      query: () => ({ url: "customers" }),
      providesTags: (res) =>
        res
          ? [
              ...res.map((c) => ({ type: "Customers" as const, id: c.AutoID })),
              { type: "Customers", id: "LIST" },
            ]
          : [{ type: "Customers", id: "LIST" }],
    }),

    // Check if a customer has invoices in draft
    checkHasInvoiceDraftForCustomer: b.query<boolean, number>({
      query: (id) => ({ url: `customers/hasinvoiceindraft/${id}/` }),
    }),

    // Details
    getCustomerById: b.query<CustomerDTO, number>({
      query: (id) => ({ url: `customers/customer/${id}` }),
      providesTags: (res, _err, id) => [{ type: "Customers", id }],
    }),

    // Update (PATCH)
    updateCustomer: b.mutation<
      CustomerDTO,
      { id: number; data: CustomerPayload }
    >({
      query: ({ id, data }) => ({
        url: `customers/customer/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "Customers", id },
        { type: "Customers", id: "LIST" },
      ],
    }),

    // Moved invoice options to invoicesApi

    // Create Customer
    createCustomer: b.mutation<CustomerDTO, CustomerPayload>({
      query: (data) => ({
        url: "customers",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "Customers", id: "LIST" }],
    }),
    archiveCustomer: b.mutation<
      { success: boolean; hasBookings?: boolean },
      number
    >({
      query: (id) => ({
        url: `customers/customer/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_res, _err, id) => [
        { type: "Customers", id },
        { type: "Customers", id: "LIST" },
      ],
    }),
    restoreCustomer: b.mutation<CustomerDTO, number>({
      query: (id) => ({
        url: `customers/customer/${id}/restore`,
        method: "POST",
      }),
      invalidatesTags: (_res, _err, id) => [
        { type: "Customers", id },
        { type: "Customers", id: "LIST" },
      ],
    }),
    getCustomersBySearchTerm: b.query<
      { id: number; label: string; sub: string | null; archived?: boolean; disabled?: boolean }[],
      { term: string; includeEx?: boolean; siteId?: number }
    >({
      query: ({ term, includeEx }) => ({
        url: `customers/search/${encodeURIComponent(term)}${includeEx ? "?includeEx=1" : ""}`,
      }),
      transformResponse: (response: CustomerDTO[]) =>
        response.map((customer) => {
          const archived = Boolean(customer.ex);
          return {
            id: customer.AutoID,
            label: customer.NameInternal ?? "",
            sub: customer.Email ?? null,
            address: [
              customer.AdressAdd,
              customer.Street,
              customer.HouseNumber,
              `${customer.ZipCode ?? ""} ${customer.City ?? ""}`.trim(),
            ]
              .filter(Boolean)
              .join(", "),
            archived,
            disabled: archived,
          };
        }),
      providesTags: [{ type: "Customers", id: "LIST" }],
    }),
  }),
});

export const {
  useGetCustomersRawQuery,
  useGetCustomerByIdQuery,
  useLazyGetCustomerByIdQuery,
  useUpdateCustomerMutation,
  useCreateCustomerMutation,
  useCheckHasInvoiceDraftForCustomerQuery,
  useLazyCheckHasInvoiceDraftForCustomerQuery,
  useGetAllCustomersQuery,
  useGetCustomersBySearchTermQuery,
  useArchiveCustomerMutation,
  useRestoreCustomerMutation,
} = customersApi;
